import { action, makeAutoObservable, reaction, runInAction } from 'mobx';
import { ManualStatus, OccupiedDurationSeconds, Presence, PresenceState, PresenceStatus } from '@ixicare/resources';

import { State } from '~/store/types';
import NotificationService from '~services/NotificationService';
import SoundService from '~services/SoundService';

/** Own presence + bulk presence cache for HCP listings. */
class PresenceStore {
  rootStore: State;

  ownPresence: PresenceStatus | null = null;
  bulk: Record<number, PresenceStatus> = {};

  // Last duration the user picked in this session — used by the picker to
  // highlight the active rung. Backend gives us `occupiedUntil` only, which
  // shifts as time passes, so we can't reliably recover the original choice
  // from it after the fact.
  lastSelectedOccupiedDuration: OccupiedDurationSeconds | null = null;

  loading: boolean = false;
  error: Error | null = null;

  /** Shared wall-clock tick. Advances every 30s; observer-wrapped components
   *  reading it re-render in lockstep. Display only shows whole minutes so a
   *  faster tick is wasted work. The expiry timer below handles the exact
   *  state transition at deadline. */
  now: number = Date.now();

  private disposeSilenceReaction: (() => void) | null = null;
  private disposeExpiryReaction: (() => void) | null = null;
  private expiryTimeout: ReturnType<typeof setTimeout> | null = null;
  private nowInterval: ReturnType<typeof setInterval> | null = null;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      refreshOwn: action.bound,
      setStatus: action.bound,
      clearStatus: action.bound,
      loadBulkPresence: action.bound,
      onSelfPresenceChanged: action.bound,
      reset: action.bound,
      rootStore: false,
    });
    this.rootStore = rootStore;

    this.disposeSilenceReaction = reaction(
      () => this.isSilenced,
      (silenced) => {
        SoundService.setPresenceSilenced(silenced);
        // Going back to Online clears the silence — but we don't trigger
        // any browser permission flow here, just stop suppressing dispatch.
        NotificationService.setSilenced(silenced);
      },
      { fireImmediately: true }
    );

    // Reschedule the local expiry timer whenever the occupied deadline shifts.
    this.disposeExpiryReaction = reaction(
      () => ({
        state: this.ownPresence?.state,
        until: this.ownPresence?.occupiedUntil ?? null,
      }),
      ({ state, until }) => this.scheduleExpiry(state, until),
      { fireImmediately: true }
    );

    if (typeof window !== 'undefined') {
      this.nowInterval = setInterval(() => {
        runInAction(() => {
          this.now = Date.now();
        });
      }, 30_000);
    }
  }

  /** Set up (or clear) the local timer that flips Occupied → Online when
   *  the deadline passes. Backend should also auto-transition; we
   *  optimistically reflect that locally and re-fetch to reconcile. */
  private scheduleExpiry(state: PresenceState | undefined, until: Date | null) {
    if (this.expiryTimeout) {
      clearTimeout(this.expiryTimeout);
      this.expiryTimeout = null;
    }
    if (state !== PresenceState.Occupied || !until) return;
    const ms = until.getTime() - Date.now();
    if (ms <= 0) {
      this.handleExpiry();
      return;
    }
    // +1s buffer so backend has a moment to commit the transition before
    // our reconciling refresh hits.
    this.expiryTimeout = setTimeout(() => this.handleExpiry(), ms + 1000);
  }

  /** Keep `bulk[userId]` aligned with `ownPresence` so list rows for the
   *  current user reflect self-changes without a separate fetch. */
  private mirrorOwnIntoBulk() {
    const userId = this.rootStore.auth?.user?.id;
    if (userId != null && this.ownPresence) {
      this.bulk = { ...this.bulk, [userId]: this.ownPresence };
    }
  }

  private handleExpiry = () => {
    runInAction(() => {
      this.ownPresence = { state: PresenceState.Online, occupiedUntil: null };
      this.lastSelectedOccupiedDuration = null;
      this.mirrorOwnIntoBulk();
    });
    void this.refreshOwn();
  };

  /** True when the user is in a manual non-default state. Suppress local
   *  sound and toast popups while this is true. */
  get isSilenced(): boolean {
    const state = this.ownPresence?.state;
    return state === PresenceState.Occupied || state === PresenceState.Unavailable;
  }

  get state(): PresenceState {
    return this.ownPresence?.state ?? PresenceState.Offline;
  }

  async refreshOwn(siteUuid?: string): Promise<void> {
    this.loading = true;
    try {
      const presence = await Presence.getOwnPresence(siteUuid);
      runInAction(() => {
        this.ownPresence = presence;
        this.error = null;
        if (presence.state !== PresenceState.Occupied) {
          this.lastSelectedOccupiedDuration = null;
        }
        this.mirrorOwnIntoBulk();
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err : new Error(String(err));
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async setStatus(
    manualStatus: ManualStatus,
    durationSeconds: OccupiedDurationSeconds | null,
    siteIds: number[] | null = null
  ): Promise<void> {
    // Optimistic update so the UI reflects the pick before the round-trip
    // completes. refreshOwn() below will reconcile against the backend.
    runInAction(() => {
      if (manualStatus === ManualStatus.Occupied && durationSeconds != null) {
        this.ownPresence = {
          state: PresenceState.Occupied,
          occupiedUntil: new Date(Date.now() + durationSeconds * 1000),
        };
        this.lastSelectedOccupiedDuration = durationSeconds;
      } else if (manualStatus === ManualStatus.Unavailable) {
        this.ownPresence = { state: PresenceState.Unavailable, occupiedUntil: null };
        this.lastSelectedOccupiedDuration = null;
      }
      this.mirrorOwnIntoBulk();
    });
    await Presence.setOwnStatus({ manualStatus, durationSeconds, siteIds });
    await this.refreshOwn();
  }

  async clearStatus(siteIds: number[] | null = null): Promise<void> {
    runInAction(() => {
      this.ownPresence = { state: PresenceState.Online, occupiedUntil: null };
      this.lastSelectedOccupiedDuration = null;
      this.mirrorOwnIntoBulk();
    });
    await Presence.clearOwnStatus(siteIds);
    await this.refreshOwn();
  }

  /** Bulk fetch for an HCP list. Merges into the existing cache so paginated
   *  pages and parallel callers don't blow each other away. */
  async loadBulkPresence(profileIds: number[], siteUuid?: string): Promise<void> {
    if (profileIds.length === 0) return;
    try {
      const result = await Presence.getBulkPresence(profileIds, siteUuid);
      runInAction(() => {
        this.bulk = { ...this.bulk, ...result };
        this.error = null;
      });
    } catch (err) {
      console.error('PresenceStore.loadBulkPresence', err);
      runInAction(() => {
        this.error = err instanceof Error ? err : new Error(String(err));
      });
    }
  }

  onSelfPresenceChanged(): void {
    void this.refreshOwn();
  }

  reset(): void {
    this.ownPresence = null;
    this.bulk = {};
    this.lastSelectedOccupiedDuration = null;
    this.error = null;
    if (this.expiryTimeout) {
      clearTimeout(this.expiryTimeout);
      this.expiryTimeout = null;
    }
    // `isSilenced` is now false; the reaction will clear the presence mute
    // request synchronously, but call services directly in case the reaction
    // is suspended during teardown.
    SoundService.setPresenceSilenced(false);
    NotificationService.setSilenced(false);
  }

  dispose(): void {
    this.disposeSilenceReaction?.();
    this.disposeSilenceReaction = null;
    this.disposeExpiryReaction?.();
    this.disposeExpiryReaction = null;
    if (this.expiryTimeout) {
      clearTimeout(this.expiryTimeout);
      this.expiryTimeout = null;
    }
    if (this.nowInterval) {
      clearInterval(this.nowInterval);
      this.nowInterval = null;
    }
  }
}

export default PresenceStore;
